import { Language } from "@safetyculture/s12-apis-connect-web/s12/common/languages_pb";
import { ContactType } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_contact_pb";
import { PriceTier } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import { PaymentMethod } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import {
  Currency,
  Period,
  SubscriptionType,
  Tier,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { WORKFLOW_CLIENT_TYPES } from "./workflow-client-types";

export const DEFAULT_REGION_CODE = "us";
export const AU_REGION_CODE = "au";
export const DEFAULT_WORKFLOW_CLIENT_TYPE =
  WORKFLOW_CLIENT_TYPES.GOVERNANCE_CLOUD;

export const periscopeThemeKey = "periscope-theme-prefers-dark";
export const periscopeThemeKeyEvent = periscopeThemeKey + "-event";
// Stores the user's light/dark/system preference. Kept separate from
// periscopeThemeKey above, which several components still read directly as
// a resolved "true"/"false" boolean for their initial render.
export const periscopeThemePreferenceKey = "periscope-theme-preference";
export const periscopeNavRedesignKey = "periscope-nav-redesign";
export const periscopeNavRedesignEvent = periscopeNavRedesignKey + "-event";
export const USER_LIST_PREFERENCE_KEY = "user-list-prefers-admins-only";

export const DEFAULT_THEME_COLOR = "#3f51b5";
export const DEFAULT_AVATAR_SIZE = 45;
export const PERISCOPE_USER_GUIDE_LINK =
  "https://safetyculture.atlassian.net/wiki/spaces/PERISCOPE/pages/2239464025/User+Guide";
export const PERISCOPE_TECHNICAL_FAQ =
  "https://safetyculture.atlassian.net/wiki/spaces/PERISCOPE/pages/1652820710/Technical+FAQ";
export const PERISCOPE_REQUEST_SUDO_ACCESS =
  "https://safetyculture.atlassian.net/wiki/spaces/PERISCOPE/pages/1880556271/FAQ#How-can-I-request-Sudo-access-for-CPR-or-customer-incident%3F";
export const ANNUAL_TO_MONTHLY_GUIDE_LINK =
  "https://safetyculture.atlassian.net/wiki/spaces/PERISCOPE/pages/2478047237/Move+a+Customer+from+Annual+to+Monthly";
export const PERISCOPE_SUDO_GUIDE_LINK =
  "https://safetyculture.atlassian.net/wiki/spaces/PERISCOPE/pages/3298853376/Sudo";
export const SC_APP_URL = "https://app.safetyculture.com";
export const DEFAULT_SNACKBAR_DURATION = 6000;
export const DEFAULT_CLIPBOARD_SB_DURATION = 3500;
export const FREE_TEAM_SEAT_LIMIT = 10;

export enum EventTypeReason {
  Clickaway = "clickaway",
}

export const NO_DISPLAY_DATA = "N/A";
export const DEFAULT_TABLE_PAGINATION = 20;

// Module trials are co-dated to the platform trial at creation, so extending the platform
// trial end date carries any currently-trialing module subscription's end date along with it.
export const PLATFORM_TRIAL_EXTENDS_MODULES_MESSAGE =
  "If any module subscriptions are currently trialing, extending this trial end date will extend theirs to match.";

// Conversely, a module subscription's own trial end date can be changed on its own without
// affecting the platform subscription.
export const MODULE_TRIAL_INDEPENDENT_MESSAGE =
  "Module subscriptions that are trialing can be updated independently of the platform subscription's trial dates.";

// The module trial date picker also enforces a 3-month cap from today (see ChangeModuleTrialDate)
// so a trial can't be repeatedly nudged out a few days at a time to run indefinitely.
export const MODULE_TRIAL_MAX_EXTENSION_MESSAGE =
  "The trial end date cannot be extended more than 3 months from today.";

// Shared across the module subscription tab's action buttons (Start Trial, Update Quantity,
// Change Trial End Date) so they render at a consistent width regardless of label length -
// wide enough to fit "CHANGE TRIAL END DATE", the longest of the three.
export const MODULE_ACTION_BUTTON_MIN_WIDTH = 250;

export const subscriptionTypeNameDisplayMapping = {
  [SubscriptionType.FREE]: "Free Indv.",
  [SubscriptionType.FREE_ORG]: "Free Plan",
  [SubscriptionType.PREMIUM_MONTHLY]: "Paid Plan (monthly)",
  [SubscriptionType.PREMIUM_ANNUAL]: "Paid Plan (annual)",
  [SubscriptionType.PREMIUM_QUARTERLY]: "Paid Plan (quarterly)",
  [SubscriptionType.PREMIUM_BIANNUAL]: "Paid Plan (biannual)",
  [SubscriptionType.PREMIUM_TWO_YEAR]: "Paid Plan (two year)",
  [SubscriptionType.PREMIUM_THREE_YEAR]: "Paid Plan (three year)",
};

export const subscriptionPremiumPeriodNameDisplayMapping = {
  [SubscriptionType.FREE]: "trial",
  [SubscriptionType.FREE_ORG]: "trial",
  [SubscriptionType.PREMIUM_MONTHLY]: "monthly",
  [SubscriptionType.PREMIUM_ANNUAL]: "annual",
  [SubscriptionType.PREMIUM_QUARTERLY]: "quarterly",
  [SubscriptionType.PREMIUM_BIANNUAL]: "biannual",
  [SubscriptionType.PREMIUM_TWO_YEAR]: "2year",
  [SubscriptionType.PREMIUM_THREE_YEAR]: "3year",
};

export const subscriptionPaymentMethodDisplayMapping = {
  [PaymentMethod.AUTOMATIC]: "Automatic",
  [PaymentMethod.INVOICE]: "Invoice",
};

export const pricingPeriodDisplayMapping = {
  [Period.MONTH]: "Monthly",
  [Period.YEAR]: "Yearly",
  [Period.QUARTER]: "Quarterly",
  [Period.HALF_YEAR]: "Half-yearly",
  [Period.TWO_YEAR]: "Two-year",
  [Period.THREE_YEAR]: "Three-year",
};

export const pricingPeriodToMonthsMapping = {
  [Period.MONTH]: 1,
  [Period.YEAR]: 12,
  [Period.QUARTER]: 3,
  [Period.HALF_YEAR]: 6,
  [Period.TWO_YEAR]: 24,
  [Period.THREE_YEAR]: 36,
};

export const priceTierDisplayMapping = {
  [PriceTier.UNSPECIFIED]: "Not specified",
  [PriceTier.PREMIUM]: "Premium",
  [PriceTier.ENTERPRISE]: "Enterprise",
};

export const subscriptionTierDisplayMapping = {
  [Tier.PREMIUM]: "Premium",
  [Tier.ENTERPRISE]: "Enterprise",
};

export enum ChargifyPlanTypeCustomFieldValue {
  PREMIUM = "Premium",
  ENTERPRISE = "Enterprise",
  UNSPECIFIED = "Unspecified",
}

export enum SudoRequestStatus {
  PENDING = "PENDING",
  USED = "USED",
  EXPIRED = "EXPIRED",
}

export const languageDisplayMapping = {
  [Language.UNSPECIFIED]: "N/A",
  [Language.EN_US]: "en-US",
  [Language.EN_GB]: "en-GB",
  [Language.DE_DE]: "de-DE",
  [Language.ES_ES]: "es-ES",
  [Language.FR_FR]: "fr-FR",
  [Language.PT_BR]: "pt-BR",
  [Language.NL_NL]: "nl-NL",
};

export enum DocPrefix {
  PrefixAudit = "audit",
  PrefixTemplate = "template",
  PrefixAction = "action",
  PrefixUser = "user",
  PrefixNotification = "ntfmsg",
  PrefixEvidence = "evidence",
  PrefixRole = "role",
  PrefixLocation = "location",
  PrefixResponseSet = "responseset",
  PrefixResponse = "response",
  PrefixPreference = "preference",
  PrefixHeadsUp = "heads_up",
  PrefixSubscription = "subscription",
  PrefixSite = "pathsite",
}

export const currencyDisplayMapping = {
  [Currency.AUD]: "AUD",
  [Currency.USD]: "USD",
  [Currency.EUR]: "EUR",
  [Currency.GBP]: "GBP",
};

export const currencySymbolMapping = {
  [Currency.AUD]: "$",
  [Currency.USD]: "$",
  [Currency.EUR]: "€",
  [Currency.GBP]: "£",
};
export enum PillStatusMode {
  EDIT,
  CREATE,
  CHECK,
}

// Human-readable labels for billing-contact types.
export const contactTypeDisplayMapping: Record<ContactType, string> = {
  [ContactType.UNSPECIFIED]: "N/A",
  [ContactType.INVOICE_DELIVERY]: "General billing contact",
  [ContactType.PO]: "PO contact",
  [ContactType.STATEMENT]: "Statement contact",
  [ContactType.INVOICE_DELIVERY_ONCE]: "Invoice delivery contact",
};

// Explanatory captions shown under each contact-type's section header,
// describing who that type of contact is for.
export const contactTypeDescriptionMapping: Record<ContactType, string> = {
  [ContactType.UNSPECIFIED]: "",
  [ContactType.INVOICE_DELIVERY]:
    "The contact(s) who receives all finance related notifications",
  [ContactType.PO]:
    "The contact(s) who should receive invoices and payment reminders before your finance team, when your business requires a purchase order",
  [ContactType.STATEMENT]:
    "The contact(s) who receives your monthly statement of account",
  [ContactType.INVOICE_DELIVERY_ONCE]:
    "The email address(s) your invoice is sent to",
};

// Per-type behaviour that drives how the billing-contact form renders a type:
// - showName: whether the first/last name fields are rendered
// - requireName: whether those names must be filled in before the form can save.
//   Invoice delivery shows names but keeps them optional, since contacts created
//   before names were captured only have an email.
export type BillingContactTypeConfig = {
  showName: boolean;
  requireName: boolean;
};

export const billingContactTypeConfig: Record<
  ContactType,
  BillingContactTypeConfig
> = {
  [ContactType.UNSPECIFIED]: {
    showName: false,
    requireName: false,
  },
  [ContactType.INVOICE_DELIVERY]: {
    showName: true,
    requireName: false,
  },
  [ContactType.PO]: { showName: true, requireName: true },
  [ContactType.STATEMENT]: {
    showName: true,
    requireName: true,
  },
  [ContactType.INVOICE_DELIVERY_ONCE]: {
    showName: true,
    requireName: true,
  },
};

// Contact types the billing-contact form currently renders/edits.
export const ENABLED_BILLING_CONTACT_TYPES: ContactType[] = [
  ContactType.INVOICE_DELIVERY,
  ContactType.STATEMENT,
  ContactType.PO,
  ContactType.INVOICE_DELIVERY_ONCE,
];

interface SafetyCultureOrgIDMap {
  [region: string]: string;
}

export const SafetyCultureOrgMap: SafetyCultureOrgIDMap = {
  au: "role_9cc5af8eff2b475c959fd99828b672e4",
  eu: "role_27939851f090424185306064949f4c37",
  us: "role_e07b6ac08a0511e29951ddd1182f65d9",
};

export const scvLink =
  "https://prod-useast-b.online.tableau.com/#/site/safetyculture/views/SingleCustomerView3WIP/OrganisationOverview?Entity=";

export enum LoginType {
  UNSPECIFIED = "N/A",
  PASSWORD = "Password only",
  SSO = "SSO only",
  ANY = "Password + SSO",
  PASSWORD_AND_SSO_ENFORCED_FOR_DOMAINS = "Password + SSO enforced for domains",
}

export enum DocumentState {
  UNSPECIFIED = "N/A",
  ACTIVE = "Active",
  ARCHIVED = "Archived",
  DELETED = "Deleted",
}

export const currencyMapping = {
  ["AUD"]: Currency.AUD,
  ["USD"]: Currency.USD,
  ["EUR"]: Currency.EUR,
  ["GBP"]: Currency.GBP,
};
